import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import _ from "lodash";

import { AddIcon, EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS, IDate, parseISO } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Dialog, { DialogTitle } from "components/Dialog";
import { MenuItem } from "components/Menu";
import Select from "components/Select";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import {
  FINANCING_EVENT_VALIDATION_SCHEMA,
  FINANCING_EVENTS_OPTIONS,
} from "pages/Deal/FinancingEvents/constants";

import {
  financingEventCreate,
  financingEventUpdate,
  useFinancingEvents,
} from "entities/Deal/sdk";

export interface IFormFinancingEventValues {
  name: string;
  event_type: string;
  closing_date: string | null;
}

export interface ICreateFinancingEventDialogButton {
  financingEventId: number | null;
  initialValues: IFormFinancingEventValues;
}

const FinancingEventModifyDialogButton = ({
  financingEventId,
  initialValues,
}: ICreateFinancingEventDialogButton) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [showLoading, hideLoading] = useLoadingBackdrop();

  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const { mutate: mutateFinancingEvents } = useFinancingEvents({ dealId });

  const onDialogOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const isUpdate = useMemo(() => {
    if (!_.isNil(financingEventId)) {
      return { title: "Edit financing event", buttonText: "Update" };
    } else {
      return { title: "New financing event", buttonText: "Create" };
    }
  }, [financingEventId]);

  const handleCreateSubmit = useCallback(
    (values: IFormFinancingEventValues, formik) => {
      showLoading();
      financingEventCreate({ dealId, data: values })
        .then(() => {
          toast.successMessage("Financing event successfully created");
          setIsOpen(false);
          formik.resetForm();
          mutateFinancingEvents();
        })
        .catch(handleInvalidRequest)
        .finally(hideLoading);
    },
    [dealId, mutateFinancingEvents, showLoading, hideLoading]
  );

  const handleUpdateSubmit = useCallback(
    (values: IFormFinancingEventValues, formik) => {
      showLoading();
      if (!_.isNil(financingEventId)) {
        financingEventUpdate({ dealId, data: values, financingEventId })
          .then(() => {
            toast.successMessage("Financing event successfully updated");
            setIsOpen(false);
            formik.resetForm();
            mutateFinancingEvents();
          })
          .catch(handleInvalidRequest)
          .finally(hideLoading);
      }
    },
    [dealId, mutateFinancingEvents, financingEventId, showLoading, hideLoading]
  );

  const formik = useFormik({
    initialValues: { ...initialValues },
    validationSchema: FINANCING_EVENT_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: _.isNil(financingEventId)
      ? handleCreateSubmit
      : handleUpdateSubmit,
  });

  const onDialogClose = useCallback(() => {
    formik.resetForm();
    setIsOpen(false);
  }, [formik]);

  const onChangeDatePicker = useCallback(
    (value: IDate | null) => {
      if (!_.isNil(value)) {
        formik.setFieldValue(
          "closing_date",
          format(value, FORMATS.BACKEND.DATE)
        );
      } else {
        formik.setFieldValue("closing_date", null);
      }
    },
    [formik]
  );

  return (
    <>
      {_.isNil(financingEventId) ? (
        <Button sx={{ width: "150px" }} onClick={onDialogOpen}>
          + New event
        </Button>
      ) : (
        <MenuItem onClick={onDialogOpen}>
          <Stack gap={1} flexDirection="row">
            <EditIcon />
            <Text variant="text2">Edit details</Text>
          </Stack>
        </MenuItem>
      )}

      <Dialog open={isOpen} onClose={onDialogClose} fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3} padding={2}>
            <DialogTitle
              sx={{
                margin: theme.spacing(2, 2, 0, 2),
                padding: 2,
                borderRadius: "8px",
                backgroundColor: colors.blue10,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon
                  style={{
                    height: "35px",
                    width: "35px",
                  }}
                />
                <Text variant="h2">{isUpdate.title}</Text>
              </Stack>
            </DialogTitle>
            <Select
              {...formik.getFieldProps("event_type")}
              inputLabel="Event type"
              error={formik.touched.event_type && !!formik.errors.event_type}
              helperText={
                formik.touched.event_type && formik.errors.event_type
                  ? formik.errors.event_type
                  : ""
              }
              options={FINANCING_EVENTS_OPTIONS}
              onChange={(event) => {
                formik.setFieldValue("event_type", event.target.value);
                formik.setFieldValue("name", event.target.value);
              }}
            />

            <TextField
              InputLabelProps={{ shrink: true }}
              {...formik.getFieldProps("name")}
              placeholder="Name your event..."
              label="Event name"
              error={formik.touched.name && !!formik.errors.name}
              onChange={(event) => {
                formik.setFieldTouched("name");
                formik.setFieldValue("name", event.target.value);
              }}
              value={
                formik.values.name === "" && !formik.touched.name
                  ? formik.values.event_type
                  : formik.values.name
              }
              helperText={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : ""
              }
            />
            <DatePicker
              label="Closing date (optional)"
              onChange={onChangeDatePicker}
              slotProps={{
                textField: {
                  error:
                    formik.touched.closing_date &&
                    !_.isEmpty(formik.errors.closing_date),
                  helperText:
                    formik.touched.closing_date && formik.errors.closing_date,
                },
                field: { clearable: true },
                inputAdornment: {
                  position: "start",
                },
              }}
              sx={{ maxWidth: "50%" }}
              value={
                !_.isNil(formik.values.closing_date)
                  ? parseISO(formik.values.closing_date)
                  : null
              }
            />
          </Stack>
          <Stack
            borderTop={1}
            borderColor={colors.gray20}
            flexDirection="row"
            justifyContent="flex-end"
            gap={1}
            padding={2}
            position="sticky"
            bottom="0"
            sx={{ backgroundColor: colors.white, zIndex: 2 }}
          >
            <Button color="secondary" variant="text" onClick={onDialogClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formik.isSubmitting}
            >
              {isUpdate.buttonText}
            </Button>
          </Stack>
        </form>
      </Dialog>
    </>
  );
};

export default FinancingEventModifyDialogButton;
